import { useCallback, useEffect, useRef, useState } from 'react';

import { CheckIcon, CopyIcon, ExternalLinkIcon, TriangleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { MODAL_INTEGRATION_API_KEY_CREATED } from '@/components/organisms/Modals/ModalConductor';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { useIntegrations } from '@/hooks/useIntegrations';
import { pushModal } from '@/redux/modals/actions';
import { CustomIntegration } from '@/shared/types/integration';
import { CHANNELS_URL, getDocsUrl } from '@/util/constants';

interface ApiSectionProps {
  integration: CustomIntegration | undefined;
  canWrite: boolean;
}

export const ApiSection = ({ integration, canWrite }: ApiSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  const { generateApiKey, generateApiKeyStatus } = useIntegrations(
    integration?.desk_id ?? '',
    integration?.integration_id ?? ''
  );

  useEffect(() => {
    return () => clearTimeout(copyTimeoutRef.current);
  }, []);

  const inboundApiUrl = integration?.integration_id
    ? `${CHANNELS_URL}/v1/custom/${integration.integration_id}`
    : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inboundApiUrl);
      setCopied(true);
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 4000);
    } catch {
      toast.error(t('common.error'));
    }
  };

  const isGenerating = generateApiKeyStatus === 'pending';

  const handleGenerateApiKey = useCallback(() => {
    if (!integration) {
      return;
    }

    generateApiKey(
      { deskId: integration.desk_id, integrationId: integration.integration_id },
      {
        onSuccess: (resp) => {
          dispatch(
            pushModal(MODAL_INTEGRATION_API_KEY_CREATED, {
              apiKey: resp.api_key,
              integrationName: integration.name,
            })
          );
        },
        onError: (error) => {
          toast.error(error.message);
        },
      }
    );
  }, [dispatch, generateApiKey, integration]);

  return (
    <div className="flex flex-col gap-2">
      <p className="px-1.5 text-sm leading-none font-medium text-foreground-muted">
        {t('integrations.custom.inbound_api')}
      </p>
      <Card className="py-0 shadow-none">
        <CardContent className="px-4 pt-4 pb-5">
          {/* Inbound API URL */}
          <div className="flex flex-col gap-2">
            <label className="text-sm leading-5 font-medium">
              {t('integrations.custom.inbound_api_url')}
            </label>
            <InputGroup size="md" className="w-full bg-background">
              <InputGroupInput
                value={inboundApiUrl}
                readOnly
                className="text-sm text-foreground-muted"
              />
              <InputGroupAddon align="inline-end">
                <InputGroupButton
                  type="button"
                  size="icon-sm"
                  onClick={handleCopy}
                  aria-label={t('common.copy')}
                  className="min-w-0"
                >
                  {copied ? (
                    <CheckIcon className="text-foreground-muted" size={16} />
                  ) : (
                    <CopyIcon className="text-foreground-muted" size={16} />
                  )}
                </InputGroupButton>
              </InputGroupAddon>
            </InputGroup>
            <p className="text-sm leading-5 text-foreground-muted">
              {t('integrations.custom.inbound_api_url_description')}
            </p>
          </div>

          {/* API Key Generation */}
          <div className="mt-4 flex flex-col gap-2 border-t pt-4">
            <label className="text-sm leading-5 font-medium">
              {t('integrations.custom.api_key')}
            </label>
            <div className="flex items-center justify-between">
              <p className="text-sm leading-5 text-foreground-muted">
                {t('integrations.custom.api_key_description')}
              </p>
              <Button
                variant="outline-neutral"
                size="sm"
                onClick={handleGenerateApiKey}
                disabled={!canWrite}
                isLoading={isGenerating}
              >
                {integration?.config?.has_apikey
                  ? t('integrations.custom.regenerate_api_key')
                  : t('integrations.custom.generate_api_key')}
              </Button>
            </div>
            {integration?.config?.has_apikey && (
              <Alert variant="warning">
                <TriangleAlertIcon className="h-4 w-4" />
                <AlertDescription>
                  {t('integrations.custom.api_key_regenerate_description')}
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Documentation link */}
          <div className="mt-4 flex justify-end">
            <Button
              variant="ghost-neutral"
              size="sm"
              className="gap-1"
              onClick={() =>
                window.open(
                  getDocsUrl('api/custom-integration/custom-integration-overview'),
                  '_blank'
                )
              }
            >
              {t('integrations.custom.installation_guide')}
              <ExternalLinkIcon className="h-4 w-4 text-foreground-muted" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
